import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { motion } from "framer-motion";
import { ArrowsClockwise, MapPin, Lightning } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

function TeamLine({ side, won }) {
  const record = side.record ? `${side.record.wins}-${side.record.losses}` : "";
  return (
    <div className={`flex items-center justify-between py-2 ${won === false ? "opacity-50" : ""}`}>
      <div className="flex items-center gap-3 min-w-0">
        <Link to={`/team/${side.id}`} className="hover:text-[#007AFF] font-head font-bold truncate" data-testid={`scorecard-team-link-${side.id}`}>
          {side.name}
        </Link>
        {record && <span className="text-[10px] font-mono-d text-zinc-500">({record})</span>}
      </div>
      <div className={`font-display text-3xl tabular-nums ${won ? "text-white" : "text-zinc-500"}`}>
        {side.score ?? "–"}
      </div>
    </div>
  );
}

function ScoreCard({ game }) {
  const final = (game.abstractState || "").toLowerCase() === "final";
  const live = (game.abstractState || "").toLowerCase() === "live";
  const awayWon = final && (game.away.score ?? 0) > (game.home.score ?? 0);
  const homeWon = final && (game.home.score ?? 0) > (game.away.score ?? 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      data-testid={`score-card-${game.gamePk}`}
      className="bg-[#121212] border border-white/10 hover:border-white/30 transition-colors rounded-sm p-4"
    >
      <div className="flex items-center justify-between mb-2 text-[10px] uppercase tracking-[0.2em]">
        <div className="flex items-center gap-1.5 text-zinc-500">
          <MapPin size={12} />
          <span className="truncate max-w-[160px]">{game.venue}</span>
        </div>
        {live ? (
          <span className="text-[#FF3B30] flex items-center gap-1 font-bold">
            <Lightning size={12} weight="fill" /> LIVE · {game.status}
          </span>
        ) : final ? (
          <span className="text-[#007AFF] font-bold">FINAL</span>
        ) : (
          <span className="text-zinc-400">{game.status}</span>
        )}
      </div>
      <div className="border-t border-white/5">
        <TeamLine side={game.away} won={final ? awayWon : null} />
        <div className="border-t border-white/5" />
        <TeamLine side={game.home} won={final ? homeWon : null} />
      </div>
    </motion.div>
  );
}

export default function Scoreboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/mlb/scoreboard");
      setData(data);
    } catch (e) {
      setErr("Could not fetch scores.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#007AFF] mb-1">Daily Slate</div>
          <h1 className="font-display text-5xl sm:text-6xl leading-none">SCOREBOARD</h1>
          <p className="text-sm text-zinc-400 mt-2 font-mono-d">
            {data ? `Games · ${data.date}` : "—"}
          </p>
        </div>
        <button
          onClick={load} data-testid="manual-refresh-button"
          className="flex items-center gap-2 px-5 py-2.5 bg-transparent border border-white/20 hover:border-white/60 text-white rounded-sm text-xs uppercase tracking-wider font-bold transition-all hover:-translate-y-0.5"
        >
          <ArrowsClockwise size={16} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {err && <div className="text-sm text-[#FF3B30] mb-4" data-testid="scoreboard-error">{err}</div>}

      {!data && !err ? (
        <div className="text-zinc-500 font-mono-d text-sm" data-testid="scoreboard-loading">Loading slate…</div>
      ) : data && data.games.length === 0 ? (
        <div className="border border-white/10 rounded-sm p-12 text-center" data-testid="scoreboard-empty">
          <div className="font-display text-3xl text-zinc-500">NO GAMES SCHEDULED</div>
          <p className="text-sm text-zinc-600 mt-2 font-mono-d">Check back tomorrow.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="scoreboard-grid">
          {data?.games?.map((g) => <ScoreCard key={g.gamePk} game={g} />)}
        </div>
      )}
    </div>
  );
}
